// Utility: compress and convert an image File to base64 data URL
// - Max width 1024, JPEG quality ~0.7 → ensures ~200-500KB per photo
// - Handles common mobile problems: HEIC images (iPhone), huge photos,
//   silent decode errors that used to bubble as an unspecific "Error al subir foto".
// Throws Error with a human-readable message ready to show to the user.

const MAX_ORIGINAL_BYTES = 15 * 1024 * 1024; // 15MB — most phone photos are 3-6MB

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('No se pudo leer el archivo. Prueba con otra foto.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('El navegador no puede leer este formato (posiblemente HEIC de iPhone). En tu móvil, cambia la cámara a formato "Más compatible" o "JPEG" en Ajustes → Cámara → Formatos.'));
    img.src = dataUrl;
  });
}

export async function fileToCompressedDataUrl(file, maxWidth = 1024, quality = 0.7) {
  if (!file) throw new Error('No se seleccionó ningún archivo.');
  if (file.size > MAX_ORIGINAL_BYTES) {
    throw new Error(`La foto es demasiado grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Prueba con una foto de menor resolución.`);
  }
  // Some browsers report HEIC as image/heic or image/heif and won't decode via <img>.
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  const isHeic = type.includes('heic') || type.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif');
  if (isHeic) {
    throw new Error('Formato HEIC no soportado. En tu iPhone: Ajustes → Cámara → Formatos → elige "Más compatible" (JPEG). Después vuelve a hacer la foto.');
  }

  const dataUrl = await readAsDataUrl(file);
  const img = await loadImage(dataUrl);
  const scale = Math.min(1, maxWidth / img.width);
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('El navegador no puede procesar imágenes. Prueba con otro navegador.');
  // Fill white so transparent PNGs don't turn black when converted to JPEG.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  let out;
  try {
    out = canvas.toDataURL('image/jpeg', quality);
  } catch (e) {
    throw new Error('El navegador rechazó la conversión (posible foto con datos protegidos). Prueba con otra foto.');
  }
  // Safety: if still too big (very tall panoramic photos), compress harder.
  if (out.length > 3_200_000 && quality > 0.4) {
    return fileToCompressedDataUrl(file, Math.min(maxWidth, 800), 0.55);
  }
  return out;
}
